import { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { DateInput } from "./DateInput";

export function AddEntryDialog({ open, onOpenChange, sessionId, licenseTypes, onSuccess, API, mannschaftOptions, lehrgangOptions }) {
  // Use provided options or defaults
  const MANNSCHAFT_OPTIONS = mannschaftOptions?.length > 0 ? mannschaftOptions : ["Damen 1", "Damen 2", "Herren 1", "Herren 2", "Herren 3", "Herren 4", "Jugend"];
  const LEHRGANG_OPTIONS = lehrgangOptions?.length > 0 ? lehrgangOptions : ["J-Lehrgang", "D-Lehrgang", "C-Lehrgang", "B/K-Lehrgang", "B-Lehrgang", "D-Fortbildung", "C-Fortbildung", "B-Fortbildung"];

  const [formData, setFormData] = useState({
    vorname: "",
    nachname: "",
    geburtsdatum: "",
    geschlecht: "",
    spieler_lizenz: "",
    schiedsrichter_lizenz: "",
    gueltig_ab: "",
    gueltig_bis: "",
    aktiv: "",
    mannschaft: "",
    lehrgang: ""
  });
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setFormData({
      vorname: "",
      nachname: "",
      geburtsdatum: "",
      geschlecht: "",
      spieler_lizenz: "",
      schiedsrichter_lizenz: "",
      gueltig_ab: "",
      gueltig_bis: "",
      aktiv: "",
      mannschaft: "",
      lehrgang: ""
    });
  };

  const handleSave = async () => {
    if (!formData.vorname || !formData.nachname) {
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API}/data/add?session_id=${sessionId}`, formData);
      resetForm();
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      console.error("Save error:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) resetForm(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold uppercase tracking-tight">
            Neuen Eintrag hinzufügen
          </DialogTitle>
          <DialogDescription>
            Fügen Sie einen neuen Eintrag zur Tabelle hinzu.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Vorname *
              </Label>
              <Input
                data-testid="add-vorname-input"
                value={formData.vorname}
                onChange={(e) => handleChange("vorname", e.target.value)}
                placeholder="Vorname"
                className="input-focus"
              />
            </div>
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Nachname *
              </Label>
              <Input
                data-testid="add-nachname-input"
                value={formData.nachname}
                onChange={(e) => handleChange("nachname", e.target.value)}
                placeholder="Nachname"
                className="input-focus"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Geburtsdatum
              </Label>
              <DateInput
                value={formData.geburtsdatum}
                onChange={(v) => handleChange("geburtsdatum", v)}
                testId="add-geburtsdatum-input"
                className="input-focus"
              />
            </div>
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Geschlecht
              </Label>
              <Select value={formData.geschlecht} onValueChange={(v) => handleChange("geschlecht", v)}>
                <SelectTrigger data-testid="add-geschlecht-select">
                  <SelectValue placeholder="Auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="männlich">männlich</SelectItem>
                  <SelectItem value="weiblich">weiblich</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
              Spieler-Lizenz
            </Label>
            <Select value={formData.spieler_lizenz} onValueChange={(v) => handleChange("spieler_lizenz", v === "_none" ? "" : v)}>
              <SelectTrigger data-testid="add-spieler-lizenz-select">
                <SelectValue placeholder="Auswählen (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Keine</SelectItem>
                {licenseTypes.spieler_lizenzen.map((l) => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
              Schiedsrichter-Lizenz
            </Label>
            <Select value={formData.schiedsrichter_lizenz} onValueChange={(v) => handleChange("schiedsrichter_lizenz", v === "_none" ? "" : v)}>
              <SelectTrigger data-testid="add-sr-lizenz-select">
                <SelectValue placeholder="Auswählen (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Keine</SelectItem>
                {licenseTypes.schiedsrichter_lizenzen.map((l) => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Gültig ab
              </Label>
              <DateInput
                value={formData.gueltig_ab}
                onChange={(v) => handleChange("gueltig_ab", v)}
                testId="add-gueltig-ab-input"
                className="input-focus"
              />
            </div>
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Gültig bis
              </Label>
              <DateInput
                value={formData.gueltig_bis}
                onChange={(v) => handleChange("gueltig_bis", v)}
                testId="add-gueltig-bis-input"
                className="input-focus"
              />
            </div>
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Aktiv
              </Label>
              <Select value={formData.aktiv || "_none"} onValueChange={(v) => handleChange("aktiv", v === "_none" ? "" : v)}>
                <SelectTrigger data-testid="add-aktiv-select">
                  <SelectValue placeholder="Auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-</SelectItem>
                  <SelectItem value="Ja">Ja</SelectItem>
                  <SelectItem value="Nein">Nein</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Mannschaft
              </Label>
              <Select value={formData.mannschaft || "_none"} onValueChange={(v) => handleChange("mannschaft", v === "_none" ? "" : v)}>
                <SelectTrigger data-testid="add-mannschaft-select">
                  <SelectValue placeholder="Auswählen (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-</SelectItem>
                  {MANNSCHAFT_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="uppercase tracking-[0.15em] text-xs font-semibold">
                Lehrgang
              </Label>
              <Select value={formData.lehrgang || "_none"} onValueChange={(v) => handleChange("lehrgang", v === "_none" ? "" : v)}>
                <SelectTrigger data-testid="add-lehrgang-select">
                  <SelectValue placeholder="Auswählen (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-</SelectItem>
                  {LEHRGANG_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            data-testid="save-entry-button"
            onClick={handleSave}
            disabled={saving || !formData.vorname || !formData.nachname}
            className="w-full"
          >
            {saving ? "Wird gespeichert..." : "Eintrag hinzufügen"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
